import { Flex, LoadingIcon } from "basis";
import { postHref } from "library/api";
import { useReducer } from "react";
import { useQuery } from "react-query";
import { Error as ErrorScreen } from "./Error";
import { MaxTriesReached } from "./MaxTriesReached";
import { OtpForm } from "./OtpForm";
import { initialState } from "./reducer/initialState";
import { otpReducer } from "./reducer/otpReducer";
import { ActionType, PageType } from "./reducer/types";
import { SessionResponse } from "./types";
import { parseSessionResponse } from "./utils";

interface Props {
  createSessionUrl: string;
  token: string;
  errorFooter?: React.ReactNode;
  onSuccess: () => void;
  hasContactUsLink?: boolean;
  onBackClick?: () => void;
}

const pageInfo = {
  [PageType.OTP_FORM]: {
    screenComponent: OtpForm,
  },
  [PageType.ERROR]: {
    screenComponent: ErrorScreen,
  },
  [PageType.MAX_TRIES_REACHED]: {
    screenComponent: MaxTriesReached,
  },
};

export const Otp = ({
  createSessionUrl,
  token,
  errorFooter,
  onSuccess,
  hasContactUsLink = true,
  onBackClick,
}: Props) => {
  const [state, dispatch] = useReducer(otpReducer, initialState);

  useQuery(
    ["session", { token }],
    async () => {
      const response = await postHref<SessionResponse>(createSessionUrl, {
        tokenId: token,
      });
      const responseData = parseSessionResponse(response.data);

      if (responseData === null) {
        throw new window.Error("Invalid session response received");
      }

      return responseData;
    },
    {
      refetchOnWindowFocus: false,
      onSuccess: ({ otpResendUrl, otpVerifyUrl, maskedMobileNumber }) => {
        dispatch({
          type: ActionType.DATA_FETCHED_SUCCESSFULLY,
          otpResendUrl,
          otpVerifyUrl,
          maskedMobileNumber,
        });
      },
      onError: () => {
        dispatch({ type: ActionType.ERROR });
      },
    },
  );

  if (state.Page === PageType.LOADING) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }

  const Component = pageInfo[state.Page].screenComponent;

  return (
    <Component
      state={state}
      dispatch={dispatch}
      errorFooter={errorFooter}
      onSuccess={onSuccess}
      hasContactUsLink={hasContactUsLink}
      onBackClick={onBackClick}
    />
  );
};
